/**
 * Error Handling Utilities
 * ========================
 * Provides consistent error handling across the Gryll.ai application.
 *
 * Features:
 * - Custom error classes for different error types
 * - API response handlers
 * - User-friendly error message formatting
 * - Safe async wrappers
 *
 * @module utils/errors
 */

// ============================================================================
// CUSTOM ERROR CLASSES
// ============================================================================

/**
 * Error thrown when an API request fails.
 * Contains additional context about the failed request.
 */
export class APIError extends Error {
  /**
   * @param {string} message - Error message
   * @param {number} status - HTTP status code
   * @param {string} endpoint - The API endpoint that failed
   * @param {Object} details - Additional error details from the response
   */
  constructor(message, status, endpoint, details = {}) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.endpoint = endpoint;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Check if this is a client error (4xx)
   */
  get isClientError() {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Check if this is a server error (5xx)
   */
  get isServerError() {
    return this.status >= 500;
  }

  /**
   * Check if this is a rate limit error
   */
  get isRateLimited() {
    return this.status === 429;
  }

  /**
   * Check if this is an authentication error
   */
  get isAuthError() {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * Error thrown when input validation fails.
 */
export class ValidationError extends Error {
  /**
   * @param {string} message - Error message
   * @param {string} field - The field that failed validation
   * @param {*} value - The invalid value (optional)
   */
  constructor(message, field, value = undefined) {
    super(message);
    this.name = "ValidationError";
    this.field = field;
    this.value = value;
  }
}

/**
 * Error thrown for authentication/authorization failures.
 */
export class AuthError extends Error {
  /**
   * @param {string} message - Error message
   * @param {string} code - Error code (e.g., 'SESSION_EXPIRED', 'INVALID_TOKEN')
   */
  constructor(message, code = "AUTH_ERROR") {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

/**
 * Error thrown when a required resource is not found.
 */
export class NotFoundError extends Error {
  /**
   * @param {string} resource - The type of resource (e.g., 'User', 'Prompt')
   * @param {string} identifier - The identifier that wasn't found
   */
  constructor(resource, identifier) {
    super(`${resource} not found: ${identifier}`);
    this.name = "NotFoundError";
    this.resource = resource;
    this.identifier = identifier;
  }
}

/**
 * Error thrown when credits are insufficient for an operation.
 */
export class InsufficientCreditsError extends Error {
  /**
   * @param {number} required - Credits required
   * @param {number} available - Credits available
   */
  constructor(required, available) {
    super(`Insufficient credits: ${available} available, ${required} required`);
    this.name = "InsufficientCreditsError";
    this.required = required;
    this.available = available;
  }
}

// ============================================================================
// API RESPONSE HANDLERS
// ============================================================================

/**
 * Handle API response and throw appropriate error if not OK.
 *
 * @param {Response} response - Fetch Response object
 * @param {string} endpoint - The endpoint URL (for error context)
 * @returns {Promise<Object>} Parsed JSON response
 * @throws {APIError} If the response is not OK
 *
 * @example
 * const response = await fetch('/api/models');
 * const data = await handleAPIResponse(response, '/api/models');
 */
export const handleAPIResponse = async (response, endpoint = "") => {
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    let errorDetails = {};

    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || errorBody.error || errorMessage;
      errorDetails = errorBody;
    } catch {
      // Response wasn't JSON, try text
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      } catch {
        // Ignore parsing errors
      }
    }

    throw new APIError(errorMessage, response.status, endpoint, errorDetails);
  }

  // Handle empty responses
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    return {};
  }

  return response.json();
};

/**
 * Wrap a fetch request with timeout support.
 *
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds (default: 30000)
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} If the request times out
 */
export const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error.name === "AbortError") {
      throw new APIError("Request timed out", 408, url);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// ============================================================================
// SAFE ASYNC WRAPPERS
// ============================================================================

/**
 * Safely execute an async function, catching errors and returning a default value.
 * Useful for non-critical operations that shouldn't break the app.
 *
 * @param {Function} asyncFn - Async function to execute
 * @param {*} fallback - Value to return if the function throws (default: null)
 * @param {Function} onError - Optional error handler callback
 * @returns {Promise<*>} Result of asyncFn or fallback value
 *
 * @example
 * const data = await safeAsync(
 *   () => fetchUserData(userId),
 *   { name: 'Guest' },
 *   (err) => logError('User fetch failed', err)
 * );
 */
export const safeAsync = async (asyncFn, fallback = null, onError = null) => {
  try {
    return await asyncFn();
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[SafeAsync Error]:", error);
    }
    if (onError) {
      onError(error);
    }
    return fallback;
  }
};

/**
 * Execute an async function and return a result tuple [data, error].
 * Inspired by Go's error handling pattern.
 *
 * @param {Promise|Function} promiseOrFn - Promise or async function to execute
 * @returns {Promise<[*, null] | [null, Error]>} Tuple of [data, null] or [null, error]
 *
 * @example
 * const [user, error] = await tryCatch(fetchUser(id));
 * if (error) {
 *   handleError(error);
 *   return;
 * }
 * console.info(user.name);
 */
export const tryCatch = async (promiseOrFn) => {
  try {
    const result =
      typeof promiseOrFn === "function"
        ? await promiseOrFn()
        : await promiseOrFn;
    return [result, null];
  } catch (error) {
    return [null, error];
  }
};

// ============================================================================
// ERROR MESSAGE FORMATTING
// ============================================================================

/**
 * User-friendly error messages for common HTTP status codes.
 */
const HTTP_ERROR_MESSAGES = {
  400: "Invalid request. Please check your input.",
  401: "Please log in to continue.",
  403: "You do not have permission for this action.",
  404: "The requested resource was not found.",
  408: "Request timed out. Please try again.",
  409: "This action conflicts with an existing resource.",
  413: "The file or data is too large.",
  422: "The data provided is invalid.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "Server error. Please try again later.",
  502: "Service temporarily unavailable. Please try again.",
  503: "Service is currently down for maintenance.",
  504: "Request timed out. Please try again.",
};

/**
 * Format an error into a user-friendly message.
 *
 * @param {Error} error - The error to format
 * @returns {string} User-friendly error message
 *
 * @example
 * try {
 *   await submitForm();
 * } catch (error) {
 *   showToast(formatErrorMessage(error), 'error');
 * }
 */
export const formatErrorMessage = (error) => {
  // Handle our custom error types
  if (error instanceof ValidationError) {
    return error.field
      ? `Invalid ${error.field}: ${error.message}`
      : error.message;
  }

  if (error instanceof APIError) {
    // Use status-specific message if available
    if (HTTP_ERROR_MESSAGES[error.status]) {
      return HTTP_ERROR_MESSAGES[error.status];
    }
    return error.message;
  }

  if (error instanceof AuthError) {
    return error.message;
  }

  if (error instanceof InsufficientCreditsError) {
    return `Not enough credits. You need ${error.required} credits but only have ${error.available}.`;
  }

  if (error instanceof NotFoundError) {
    return `${error.resource} not found.`;
  }

  // Network errors
  if (error.name === "TypeError" && error.message === "Failed to fetch") {
    return "Unable to connect. Please check your internet connection.";
  }

  // Abort errors
  if (error.name === "AbortError") {
    return "Request was cancelled.";
  }

  // Default message
  return error.message || "An unexpected error occurred. Please try again.";
};

/**
 * Get the appropriate error severity level.
 * Useful for toast notifications or logging.
 *
 * @param {Error} error - The error to analyze
 * @returns {'info' | 'warning' | 'error'} Severity level
 */
export const getErrorSeverity = (error) => {
  if (error instanceof ValidationError) {
    return "warning";
  }

  if (error instanceof APIError) {
    if (error.isClientError) return "warning";
    if (error.isServerError) return "error";
    if (error.isRateLimited) return "info";
  }

  if (error instanceof InsufficientCreditsError) {
    return "warning";
  }

  return "error";
};

// ============================================================================
// ERROR LOGGING
// ============================================================================

/**
 * Log an error with context (development only).
 * In production, this could be extended to send to an error tracking service.
 *
 * @param {string} context - Where the error occurred
 * @param {Error} error - The error object
 * @param {Object} metadata - Additional context data
 */
export const logError = (context, error, metadata = {}) => {
  if (import.meta.env.DEV) {
    console.info(`🚨 Error in ${context}`);
    console.error("Error:", error);
    console.error("Message:", error.message);
    if (Object.keys(metadata).length > 0) {
      console.error("Metadata:", metadata);
    }
    if (error.stack) {
      console.error("Stack:", error.stack);
    }
    // End error group
  }

  // In production, you might want to send to an error tracking service:
  // if (import.meta.env.PROD) {
  //   errorTrackingService.capture(error, { context, ...metadata });
  // }
};

// ============================================================================
// RETRY LOGIC
// ============================================================================

/**
 * Retry an async function with exponential backoff.
 *
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {Function} options.shouldRetry - Function to determine if should retry (default: always)
 * @returns {Promise<*>} Result of the function
 *
 * @example
 * const data = await retry(
 *   () => fetchData(),
 *   { maxRetries: 3, shouldRetry: (err) => err.status === 429 }
 * );
 */
export const retry = async (fn, options = {}) => {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    shouldRetry = () => true,
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay);

      if (import.meta.env.DEV) {
        console.info(
          `Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`,
        );
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Error classes
  APIError,
  ValidationError,
  AuthError,
  NotFoundError,
  InsufficientCreditsError,

  // Handlers
  handleAPIResponse,
  fetchWithTimeout,

  // Safe wrappers
  safeAsync,
  tryCatch,

  // Formatting
  formatErrorMessage,
  getErrorSeverity,

  // Logging
  logError,

  // Retry
  retry,
};
